import {
	acceptsFollowUp,
	formatIstDateTime,
	formatQueryId,
	parseTimestamp,
	workStatusLabel,
} from "@anthra/platform/domain"
import { isAccessDenied, toDisplayable } from "@anthra/platform/errors"
import { Breadcrumb, BreadcrumbCurrent, BreadcrumbItem } from "@anthra/ui/breadcrumb"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { AccessDenied } from "@anthra/ui/feedback"
import { ExpandableRichText } from "@anthra/ui/rich-text"
import { ScrollFab } from "@anthra/ui/scroll-fab"
import { Skeleton, SkeletonText } from "@anthra/ui/skeleton"
import { createFileRoute, Link } from "@tanstack/react-router"
import { CheckIcon, InfoIcon, TriangleAlertIcon, WifiOffIcon } from "lucide-react"
import { useEffect, useState } from "react"

import { AttachmentList } from "@/components/attachment-list"
import { useMarkSeen } from "@/features/activity/use-activity"
import { FeedbackPrompt } from "@/features/queries/feedback-prompt"
import { FollowUpComposer } from "@/features/queries/follow-up-composer"
import { QueryThread } from "@/features/queries/query-thread"
import { useQueryDetail } from "@/features/queries/use-queries"

/**
 * One query, with its thread (US-00-002.9/.12/.13, US-01-003.9/.12/.13).
 *
 * Built against screens/query-detail.html: the id/title/status head, the case
 * facts in a right-hand card on desktop, the thread and the composer.
 *
 * NO EDIT AND NO DELETE, ANYWHERE ON THIS SCREEN. The title, the category, the
 * description and the attachments are fixed once submitted; adding information
 * is done through a follow-up message, and the API exposes no route for the rest
 * (US-00-002.25).
 *
 * A Closed query is fully readable, has no composer, and says where to go next
 * (US-00-002.12). `closeReason` never reaches this screen — the user-facing
 * payload does not carry it (US-01-003.13).
 */
export const Route = createFileRoute("/_app/queries/$queryId")({
	component: QueryDetail,
})

/**
 * The browser's own connectivity signal (US-00-002.21/.27): a follow-up typed
 * with no network shows a retryable message rather than failing silently, and
 * the offline strip below is what tells the person why before they even try.
 */
function useOnline(): boolean {
	const [online, setOnline] = useState(() =>
		typeof navigator === "undefined" ? true : navigator.onLine,
	)

	useEffect(() => {
		const goOnline = () => {
			setOnline(true)
		}
		const goOffline = () => {
			setOnline(false)
		}
		window.addEventListener("online", goOnline)
		window.addEventListener("offline", goOffline)
		return () => {
			window.removeEventListener("online", goOnline)
			window.removeEventListener("offline", goOffline)
		}
	}, [])

	return online
}

/**
 * A follow-up's confirmation, shown once and then gone (screens/query-detail.html's
 * success state, `.toastwrap`/`.toast`) — a quiet acknowledgement that the
 * message reached the thread and the expert was notified, not a modal the
 * person has to dismiss to keep reading.
 */
function useFollowUpToast(): { message: string | null; announce: (sequence: number) => void } {
	const [message, setMessage] = useState<string | null>(null)

	useEffect(() => {
		if (message === null) return undefined
		const timer = setTimeout(() => {
			setMessage(null)
		}, 6000)
		return () => {
			clearTimeout(timer)
		}
	}, [message])

	return {
		message,
		announce: sequence => {
			setMessage(
				`Follow-up posted to query ${formatQueryId(sequence)}. The expert has been notified on WhatsApp.`,
			)
		},
	}
}

/**
 * A thread opens at its top, the question and the first messages, so the
 * conversation reads from the start (client feedback; it used to open at
 * its latest message — the composer is home
 * (US-00-002.9). Runs once the real content has painted, not the skeleton, so
 * it scrolls to the actual bottom rather than a placeholder's.
 */
function useScrollToLatestOnLoad(ready: boolean, key: string): void {
	useEffect(() => {
		if (!ready) return undefined
		// Deferred a tick: the router's own navigation-complete handler resets
		// scroll to the top AFTER this effect would otherwise run, undoing it.
		// Running one macrotask later lands after that reset.
		const timer = setTimeout(() => {
			window.scrollTo({ top: 0 })
		}, 0)
		return () => {
			clearTimeout(timer)
		}
	}, [ready, key])
}

function QueryDetail() {
	const online = useOnline()
	const { queryId } = Route.useParams()
	const query = useQueryDetail(queryId)
	useMarkSeen("query", queryId, query.isSuccess)
	const followUpToast = useFollowUpToast()
	useScrollToLatestOnLoad(query.isSuccess, queryId)

	if (query.isPending) {
		return (
			<div className="flex flex-col gap-6">
				<p
					role="status"
					className="sr-only"
				>
					Loading your query.
				</p>
				<div
					className="flex flex-col gap-6"
					aria-hidden="true"
				>
					<div className="border-border flex flex-wrap items-start justify-between gap-4 border-b pb-6">
						<div className="flex min-w-0 flex-1 flex-col gap-3">
							<Skeleton className="h-4 w-24" />
							<Skeleton className="h-8 w-3/4" />
							<SkeletonText lines={2} />
							<div className="flex flex-wrap gap-3">
								<Skeleton className="h-6 w-24 rounded-sm" />
								<Skeleton className="h-4 w-40" />
							</div>
						</div>
						<Skeleton className="rounded-pill h-7 w-24 shrink-0" />
					</div>
					<div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_var(--width-rail)] lg:items-start">
						<div className="flex flex-col gap-4">
							<Skeleton className="h-24 w-full" />
							<Skeleton className="h-24 w-3/4" />
						</div>
						<Skeleton className="h-40 w-full rounded-lg" />
					</div>
				</div>
			</div>
		)
	}
	if (query.isError) {
		// A deep link to somebody else's query — or to one that was never there —
		// lands here, and both look identical (US-00-006.18/.19).
		if (isAccessDenied(query.error)) {
			return (
				<AccessDenied
					backTo={
						<Link
							to="/queries"
							className="text-primary text-sm underline"
						>
							Back to My Queries
						</Link>
					}
				/>
			)
		}
		const displayable = toDisplayable(query.error)
		return (
			<div className="flex flex-col gap-4">
				<div className="border-border flex flex-col gap-2 border-b pb-6">
					<h2 className="text-card-title text-foreground">{displayable.title}</h2>
					<p className="text-description text-subtle-foreground">{displayable.message}</p>
				</div>
				<div
					className="border-danger-border bg-danger-soft flex items-start gap-3 rounded-lg border p-5"
					role="alert"
				>
					<TriangleAlertIcon
						className="text-danger mt-0.5 size-5 shrink-0"
						aria-hidden="true"
					/>
					<div className="flex flex-col gap-2">
						<h3 className="text-card-title text-foreground">
							Couldn&rsquo;t load this query
						</h3>
						<p className="text-description text-subtle-foreground">
							{displayable.message}
						</p>
						{displayable.canRetry ? (
							<div className="flex flex-wrap gap-2 pt-1">
								<button
									type="button"
									className={cn(buttonVariants({ variant: "secondary" }))}
									onClick={() => {
										void query.refetch()
									}}
								>
									Try again
								</button>
							</div>
						) : null}
					</div>
				</div>
			</div>
		)
	}

	const detail = query.data
	const submitted = parseTimestamp(detail.submittedAt)
	const lastUpdated = parseTimestamp(detail.lastUpdatedAt)

	return (
		<div className="flex flex-col gap-6">
			<ScrollFab />

			{online ? null : (
				<div
					className="border-warning-border bg-warning-soft text-description text-foreground flex flex-wrap items-center gap-3 rounded-md border px-4 py-3"
					role="status"
				>
					<WifiOffIcon
						className="text-warning size-[18px] shrink-0"
						aria-hidden="true"
					/>
					<span className="min-w-0 flex-1">
						You are offline. Your draft is kept here and nothing has been sent yet.
					</span>
					<button
						type="button"
						className={cn(buttonVariants({ variant: "secondary" }))}
						onClick={() => {
							void query.refetch()
						}}
					>
						Check connection
					</button>
				</div>
			)}

			{followUpToast.message === null ? null : (
				<div className="z-toast fixed right-4 bottom-20 lg:bottom-4">
					<div
						className="bg-surface-inverse text-text-inverse flex items-start gap-3 rounded-md px-5 py-4 shadow-lg"
						role="status"
					>
						<CheckIcon
							className="text-success mt-0.5 size-4 shrink-0"
							aria-hidden="true"
						/>
						<p className="text-description">{followUpToast.message}</p>
					</div>
				</div>
			)}

			<Breadcrumb>
				<BreadcrumbItem>
					<Link
						to="/queries"
						className="hover:text-foreground underline-offset-2 hover:underline"
					>
						My questions
					</Link>
				</BreadcrumbItem>
				<BreadcrumbItem last>
					<BreadcrumbCurrent>{formatQueryId(detail.sequence)}</BreadcrumbCurrent>
				</BreadcrumbItem>
			</Breadcrumb>

			<div className="flex flex-col gap-3">
				<div className="flex flex-col gap-1">
					<p className="text-field-label text-primary">Title</p>
					<h2 className="text-page-title text-heading min-w-0 break-all">
						{detail.title}
					</h2>
				</div>
				<dl className="flex flex-wrap gap-x-8 gap-y-3">
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Status of the request</dt>
						<dd className="text-description text-foreground">
							{workStatusLabel(detail.status)}
						</dd>
					</div>

					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Submitted at</dt>
						<dd className="text-description text-foreground tabular-nums">
							{submitted ? formatIstDateTime(submitted) : "—"}
						</dd>
					</div>
					{lastUpdated ? (
						<div className="flex flex-col gap-1">
							<dt className="text-meta text-muted">Last updated</dt>
							<dd className="text-description text-foreground tabular-nums">
								{formatIstDateTime(lastUpdated)}
							</dd>
						</div>
					) : null}
				</dl>
			</div>

			{/* WHAT THE READER ACTUALLY WROTE, ACROSS THE FULL COLUMN.
			    This was a `text-description` paragraph capped at --measure-prose
			    inside the head, which itself sat in a two-thirds column beside a
			    facts rail: a 68-character measure at 14px inside 66% of the page,
			    which is the "description only fills about half the width" the
			    client reported. The rail is gone (it repeated the head), so the
			    block now spans the column; the TEXT keeps a measure because a
			    1000px line is not readable, but the sheet it sits on does not,
			    so there is no longer a band of empty page beside it.

			    Set at --text-body rather than the description size: this is the
			    case the whole screen is about, and it was two steps smaller than
			    the expert's reply underneath it. */}
			{detail.description === "" ? null : (
				<div className="flex flex-col gap-2">
					<p className="text-field-label text-primary">Description</p>
					<div className="border-border bg-surface-sunken border-l-accent rounded-card border border-l-[3px] p-6">
						{/* `description` is rich-text markup, sanitised server-side, the
					    SAME renderer as a reply. There is no separate plain field to
					    fall back to — a not-yet-migrated client's plain text is
					    well-formed markup with no tags, so this renders it unchanged. */}
						<ExpandableRichText
							className="max-w-[80ch]"
							html={detail.description}
						/>
					</div>
				</div>
			)}

			{detail.attachments.length > 0 ? (
				<AttachmentList
					attachments={detail.attachments}
					variant="thread"
					queryId={queryId}
				/>
			) : null}

			{/* ONE COLUMN. This was a grid with a --width-rail facts panel beside
			    it, and a <details> carrying the same three facts on a phone. Both
			    listed Reference, Category and Submitted — and all three are in the
			    head above: the reference in the breadcrumb, the other two in the
			    meta row. So the rail was a second, worse rendering of information
			    the reader had already passed, and it cost the thread a third of
			    the page to say it.

			    The one thing in there that was NOT a repeat — the note about what
			    can and cannot be edited — has moved to the follow-up composer,
			    which is the moment it is actually about. */}
			<div className="flex flex-col gap-8 lg:pt-2">
				<div className="flex min-w-0 flex-col gap-6">
					<section
						className="flex min-w-0 flex-col gap-3"
						aria-label="Conversation"
					>
						{/* The ROUTE param, not `detail.id`: the viewer reaches the
						    parent through `useQueryDetail`, which is keyed the same way
						    this screen is. */}
						<QueryThread
							messages={detail.messages}
							queryId={queryId}
						/>
					</section>

					{/* Eligible the moment the query is Answered, and replaced by a
					    read-only summary once it has been given (US-00-002.13/.14). */}
					{detail.status === "answered" || detail.feedback !== null ? (
						<FeedbackPrompt
							queryId={detail.id}
							title={detail.title}
							feedback={detail.feedback}
						/>
					) : null}

					{acceptsFollowUp(detail.status) ? (
						<details className="group border-border bg-surface flex flex-col gap-2 rounded-lg border p-5 shadow-sm">
							{/* AN ACCORDION, CLOSED UNTIL ASKED FOR (client feedback: the box
							    was very big). The composer stays mounted while closed, so a
							    half-written follow-up is not lost by folding it away. */}
							<summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
								<h3 className="text-card-title text-foreground text-primary">
									Add a follow-up
								</h3>
								<svg
									className="text-muted size-5 shrink-0 transition-transform group-open:rotate-180"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="m6 9 6 6 6-6" />
								</svg>
							</summary>
							<div className="mt-2 flex flex-col gap-2">
								<p className="text-meta text-muted">
									Your expert is notified on WhatsApp. Everything you add stays on
									this thread.
								</p>
								{/* Was in the facts rail, where it explained an edit the reader
							    was not attempting. It belongs beside the composer: this is
							    the control it is describing the limits of. */}
								<div className="border-info-border bg-info-soft text-meta text-foreground flex items-start gap-3 rounded-md border p-4">
									<InfoIcon
										className="text-info mt-0.5 size-4 shrink-0"
										aria-hidden="true"
									/>
									<span>
										Title, category, description and the original attachments
										cannot be edited. Add anything new as a follow-up and it
										stays with the case.
									</span>
								</div>
								<FollowUpComposer
									queryId={detail.id}
									onPosted={() => {
										followUpToast.announce(detail.sequence)
										// If the query is answered and a follow-up is posted, change status to in_progress
										if (detail.status === "answered") {
											void query.refetch()
										}
									}}
								/>
							</div>
						</details>
					) : (
						<div className="border-border-strong bg-surface-sunken text-description text-secondary-foreground flex flex-col items-center gap-3 rounded-lg border border-dashed p-6 text-center">
							<p className="max-w-[var(--measure-prose-narrow)]">
								This query is closed. Raise a new query if you need more help with
								this notice.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
